import { SPH_STRUCT_TANIMLARI, SPH_KERNEL_FONKSIYONLARI, SPH_GRID_YARDIMCI, SPH_SDF_YARDIMCI, SPH_ORTAK_BINDING_TANIMLARI, buildShader } from './sph-ortak-shaders.wgsl';

/**
 * SDF Collision Response Shader
 * Handles particle-mesh collision detection and response using signed distance fields
 */
export const SDF_COLLISION_PASS = buildShader(`
${SPH_STRUCT_TANIMLARI}
${SPH_SDF_YARDIMCI}
${SPH_ORTAK_BINDING_TANIMLARI}

// SDF-specific bindings at group(3)
@group(3) @binding(0) var<storage, read> sdfData: array<f32>;
@group(3) @binding(1) var<storage, read> sdfInfo: SDFInfo;

@compute @workgroup_size(256)
fn sdfCollision(@builtin(global_invocation_id) globalId: vec3<u32>) {
  let idx = globalId.x;
  if (idx >= params.activeCount) {
    return;
  }

  // Read particle data
  var pos = positionMass[idx].xyz;
  var vel = velocityAge[idx].xyz;
  let mass = positionMass[idx].w;

  // Extract SDF parameters from sdfInfo struct
  let sdfOrigin = vec3<f32>(sdfInfo.origin_x, sdfInfo.origin_y, sdfInfo.origin_z);
  let sdfDims = vec3<u32>(sdfInfo.dim_x, sdfInfo.dim_y, sdfInfo.dim_z);

  // Sample SDF at particle position
  let distance = sdfSampleTrilinear(pos, sdfOrigin, sdfInfo.cellSize, sdfDims, &sdfData);

  // Check if particle is inside mesh (distance < 0)
  if (distance < 0.0) {
    // Compute surface normal from SDF gradient
    let normal = normalize(sdfGradient(pos, sdfOrigin, sdfInfo.cellSize, sdfDims, &sdfData));

    // Push particle out to surface
    let penetrationDepth = abs(distance);
    pos += normal * penetrationDepth;

    // Decompose velocity into normal and tangential components
    let velocityNormal = dot(vel, normal) * normal;
    let velocityTangent = vel - velocityNormal;

    // Apply boundary conditions
    let restitution = params.boundaryDamping; // Use as coefficient of restitution
    let friction = 0.9; // Tangential friction coefficient

    // Reflect velocity: tangent dampened by friction, normal reversed and dampened
    vel = velocityTangent * friction - velocityNormal * restitution;

    // Write back updated particle data
    positionMass[idx] = vec4<f32>(pos, mass);
    velocityAge[idx] = vec4<f32>(vel, velocityAge[idx].w);
  }
}
`);

/**
 * Mesh to SDF Conversion Shader
 * Converts triangle mesh into a 3D signed distance field for collision detection
 */
export const SDF_MESH_TO_SDF_PASS = buildShader(`
${SPH_STRUCT_TANIMLARI}

// SDF generation parameters
struct SDFGenParams {
  originX: f32,
  originY: f32,
  originZ: f32,
  cellSize: f32,
  dimX: u32,
  dimY: u32,
  dimZ: u32,
  triCount: u32,
}

@group(3) @binding(2) var<storage, read> triangleVertices: array<vec4<f32>>;
@group(3) @binding(3) var<storage, read_write> sdfOutput: array<f32>;
@group(3) @binding(4) var<uniform> sdfGenParams: SDFGenParams;

// Point-to-triangle distance calculation
fn pointToTriangleDistance(p: vec3<f32>, v0: vec3<f32>, v1: vec3<f32>, v2: vec3<f32>) -> f32 {
  let e0 = v1 - v0;
  let e1 = v2 - v0;
  let e2 = p - v0;

  let a = dot(e0, e0);
  let b = dot(e0, e1);
  let c = dot(e1, e1);
  let d = dot(e0, e2);
  let e = dot(e1, e2);

  let det = a * c - b * b;
  var s = b * e - c * d;
  var t = b * d - a * e;

  if (s + t <= det) {
    if (s < 0.0) {
      if (t < 0.0) {
        // Region 4
        if (d < 0.0) {
          t = 0.0;
          s = clamp(-d / a, 0.0, 1.0);
        } else {
          s = 0.0;
          t = clamp(e / c, 0.0, 1.0);
        }
      } else {
        // Region 3
        s = 0.0;
        t = clamp(e / c, 0.0, 1.0);
      }
    } else if (t < 0.0) {
      // Region 5
      t = 0.0;
      s = clamp(-d / a, 0.0, 1.0);
    } else {
      // Region 0 (inside triangle)
      let invDet = 1.0 / det;
      s *= invDet;
      t *= invDet;
    }
  } else {
    if (s < 0.0) {
      // Region 2
      let tmp0 = b + d;
      let tmp1 = c + e;
      if (tmp1 > tmp0) {
        let numer = tmp1 - tmp0;
        let denom = a - 2.0 * b + c;
        s = clamp(numer / denom, 0.0, 1.0);
        t = 1.0 - s;
      } else {
        s = 0.0;
        t = clamp(e / c, 0.0, 1.0);
      }
    } else if (t < 0.0) {
      // Region 6
      if (a + d > b + e) {
        let numer = c + e - b - d;
        let denom = a - 2.0 * b + c;
        s = clamp(numer / denom, 0.0, 1.0);
        t = 1.0 - s;
      } else {
        t = 0.0;
        s = clamp(-d / a, 0.0, 1.0);
      }
    } else {
      // Region 1
      let numer = c + e - b - d;
      let denom = a - 2.0 * b + c;
      s = clamp(numer / denom, 0.0, 1.0);
      t = 1.0 - s;
    }
  }

  let closest = v0 + s * e0 + t * e1;
  return length(p - closest);
}

// Compute sign based on triangle normal orientation
fn computeSign(p: vec3<f32>, v0: vec3<f32>, v1: vec3<f32>, v2: vec3<f32>) -> f32 {
  let e0 = v1 - v0;
  let e1 = v2 - v0;
  let normal = normalize(cross(e0, e1));
  let toPoint = p - v0;

  // Positive inside (convention), negative outside
  return select(-1.0, 1.0, dot(normal, toPoint) < 0.0);
}

@compute @workgroup_size(256)
fn meshToSDF(@builtin(global_invocation_id) globalId: vec3<u32>) {
  let idx = globalId.x;
  let totalVoxels = sdfGenParams.dimX * sdfGenParams.dimY * sdfGenParams.dimZ;

  if (idx >= totalVoxels) {
    return;
  }

  // Convert 1D index to 3D grid coordinates
  let z = idx / (sdfGenParams.dimX * sdfGenParams.dimY);
  let remainder = idx % (sdfGenParams.dimX * sdfGenParams.dimY);
  let y = remainder / sdfGenParams.dimX;
  let x = remainder % sdfGenParams.dimX;

  // Compute world position of this voxel
  let voxelPos = vec3<f32>(
    sdfGenParams.originX + f32(x) * sdfGenParams.cellSize,
    sdfGenParams.originY + f32(y) * sdfGenParams.cellSize,
    sdfGenParams.originZ + f32(z) * sdfGenParams.cellSize
  );

  // Find minimum distance to all triangles
  var minDistance = 1e10;
  var sign = 1.0;

  for (var triIdx = 0u; triIdx < sdfGenParams.triCount; triIdx++) {
    let vertexIdx = triIdx * 3u;
    let v0 = triangleVertices[vertexIdx].xyz;
    let v1 = triangleVertices[vertexIdx + 1u].xyz;
    let v2 = triangleVertices[vertexIdx + 2u].xyz;

    let dist = pointToTriangleDistance(voxelPos, v0, v1, v2);

    if (dist < minDistance) {
      minDistance = dist;
      sign = computeSign(voxelPos, v0, v1, v2);
    }
  }

  // Write signed distance to output buffer
  sdfOutput[idx] = sign * minDistance;
}
`);

/**
 * Shader metadata for SDF operations
 */
export const SDF_SHADER_METADATA = {
  passes: {
    sdfCollision: {
      name: 'SDF Collision Response',
      description: 'Detects and resolves particle-mesh collisions using signed distance fields',
      workgroupSize: 256,
      requiredBindings: ['sdfData', 'sdfInfo', 'positionMass', 'velocityAge', 'params', 'uniforms']
    },
    meshToSDF: {
      name: 'Mesh to SDF Conversion',
      description: 'Converts triangle mesh into 3D signed distance field for collision detection',
      workgroupSize: 256,
      requiredBindings: ['triangleVertices', 'sdfOutput', 'sdfGenParams']
    }
  },
  version: '1.0.0',
  author: 'FikoYT SPH System',
  optimizations: [
    'Trilinear interpolation for smooth SDF sampling',
    'Gradient-based normal computation',
    'Separate normal/tangent velocity decomposition',
    'Brute-force triangle distance with early exit potential'
  ]
};
